import * as React from 'react'
import { connect } from 'react-redux'
import { handleOpeningHours } from '../../../utils/openingHours'
import type { RootState, Dispatch } from '../../../app/store'
import type { MenuT, StatusT } from '../../../models/menus'
import Page from '../../layout/Page'
import Loading from '../../Loading'
import MenuItem from '../../menus/item'
import { useLocation } from 'react-router-dom'
import styles from './Menus.module.css'
import { StoreT } from '../../../models/store'
import MessageBanner from '../../MessageBanner'
import Modal from '../../Modal'
import eatinIcon from '../../../images/eatin.svg'
import takeawayIcon from '../../../images/takeaway.svg'
import DeliveryItem from '../../DeliveryItem'
import { DELIVERY_ITEM_TYPE, DELIVERY_TYPE, DeliveryMethodT, DeliveryMethodsT, DeliveryType, getDeliveryDisplayText, isAvailable } from '../../../utils/deliveryHelper'

interface MenusT {
  status: StatusT,
  menuItems: Array<MenuT>
  store: StoreT | null
  selectDeliveryType: (payload: { type: typeof DELIVERY_TYPE.EATIN | typeof DELIVERY_TYPE.TAKEAWAY, deliveryProviderId?: string, deliveryType?: number }) => void
  fetchActiveDeliveryMethods: (storeId: string) => Promise<DeliveryMethodsT>
  deliveryType: DeliveryType
  tableNumber: string | undefined
}

function Menus ({ status, menuItems, store, deliveryType, tableNumber, selectDeliveryType, fetchActiveDeliveryMethods }: MenusT) {
  const location = useLocation()
  const { startupMessage } = store || {}
  const [showDeliveryModal, setShowDeliveryModal] = React.useState(!deliveryType || deliveryType === 'IDLE')
  const [deliveryOption, setDeliveryOption] = React.useState<'eatin' | 'takeaway' | null>(null)
  const [deliveryMethods, setDeliveryMethods] = React.useState<DeliveryMethodsT>([])
  const [subMethods, setSubMethods] = React.useState<DeliveryMethodsT>([])
  const [showSubMethodOptions, setShowSubMethodOptions] = React.useState(false)
  const [selectedSubMethod, setSelectedSubMethod] = React.useState<DeliveryMethodT | null>(null)
  const [loading, setLoading] = React.useState(false)

  const deliveryDisplayText = getDeliveryDisplayText(deliveryOption)
  
  React.useEffect(() => {
    if (deliveryType === DELIVERY_TYPE.IDLE && !tableNumber) {
      setShowDeliveryModal(true)
      setDeliveryOption(null)
      setShowSubMethodOptions(false)
      setSelectedSubMethod(null)
      setSubMethods([])
    }
  }, [deliveryType, tableNumber])

  React.useEffect(() => {
    async function getDeliveryOptions (storeId: string) {
      if (tableNumber && deliveryType === DELIVERY_TYPE.IDLE) {
        // Handle QR code for table number (default to EATIN)
        setDeliveryOption('eatin')
        selectDeliveryType({ type: DELIVERY_TYPE.EATIN })
        setShowDeliveryModal(false)
        return
      }

      if (deliveryType !== DELIVERY_TYPE.IDLE) return

      const methods: DeliveryMethodsT = await fetchActiveDeliveryMethods(storeId)
      setDeliveryMethods(methods.filter(method => method.availabilityHours.some(day => isAvailable(day))))
    }
    store?.id && getDeliveryOptions(store.id)
  }, [deliveryType, tableNumber])

  const handleDeliveryChoice = (selectedOption: 'eatin' | 'takeaway') => {
    setDeliveryOption(selectedOption)

    // Filter sub delivery methods based on EATIN or TAKEAWAY
    const matchingSubMethods = deliveryMethods.filter((method) => {
      if (!method.deliveryType) return false
      return selectedOption === 'eatin' ? method.deliveryType === 'EatOnSite' : method.deliveryType === 'Pickup'
    })

    if (matchingSubMethods.length > 0) {
      setSubMethods(matchingSubMethods)
      setShowSubMethodOptions(true)
    } else {
      // If no sub methods, select delivery type, close modal and proceed
      const selectedDeliveryType = selectedOption === 'eatin' ? DELIVERY_TYPE.EATIN : DELIVERY_TYPE.TAKEAWAY
      const itemType = selectedOption === 'eatin' ? DELIVERY_ITEM_TYPE.EATIN : DELIVERY_ITEM_TYPE.TAKEAWAY_DEFAULT
      setShowDeliveryModal(false)
      selectDeliveryType({ 
        type: selectedDeliveryType,
        deliveryType: itemType,
      })
    }
  }

  const handleSubDeliveryChoice = async (method: DeliveryMethodT) => {
    setLoading(true)
    setSelectedSubMethod(method)

    try {
      const isEatIn = method.deliveryType === 'EatOnSite'
      const selectedDeliveryType = isEatIn ? DELIVERY_TYPE.EATIN : DELIVERY_TYPE.TAKEAWAY
      if (store?.id) {
        const itemType = isEatIn ? DELIVERY_ITEM_TYPE.EATIN : DELIVERY_ITEM_TYPE.TAKEAWAY_METHOD
        selectDeliveryType({ 
          type: selectedDeliveryType, 
          deliveryProviderId: method.id,
          deliveryType: itemType
        })
      }
    } finally {
      setLoading(false)
      setShowDeliveryModal(false)
    }
  }

  return (
    <Page withTopNav={true} withCancelButton={true} withCart={true}>
      <Modal active={showDeliveryModal} maxWidth="400px">
        <div className={styles.deliveryModal}>
          {startupMessage && !deliveryOption && <MessageBanner title={startupMessage.title} description={startupMessage.text} />}
          
          {!showSubMethodOptions ? (
            <div className={styles.deliveryModalButtons}>
              <button className={styles.deliveryModalButton} onClick={() => handleDeliveryChoice('eatin')}>
                <img src={eatinIcon} alt="" className={styles.deliveryModalIcon} />
                Äta här
              </button>
              <button className={styles.deliveryModalButton} onClick={() => handleDeliveryChoice('takeaway')}>
                <img src={takeawayIcon} alt="" className={styles.deliveryModalIcon} />
                Ta med
              </button>
            </div>
          ) : (
            <div className={styles.methodOptions}>
              <h2 className={styles.methodOptionsTitle}>{deliveryDisplayText}</h2>
              <div className={styles.methodOptionsList}>
                {subMethods.map((option) => {
                  const messages: string[] = []
                  option.deliveryTime && messages.push(option.deliveryTime)
                  option.description && messages.push(option.description)

                  return (
                    <DeliveryItem
                      loading={selectedSubMethod?.id === option.id && loading}
                      key={option.id}
                      id={option.id}
                      active={selectedSubMethod?.id === option.id}
                      displayName={option.name}
                      messages={messages}
                      onSelect={() => handleSubDeliveryChoice(option)}
                      price={option.price}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </Modal>
      {status === 'FETCHING' && (
        <Loading heading="Laddar menyer" />
      )}
      <div className={styles.content}>
        <h1 className={styles.heading}>Alla menyer {menuItems && menuItems.length > 5 && (
          <span>({menuItems.length.toString()})</span>
        )}</h1>
        <ul className={styles.list}>
          {menuItems.map(({ id, name, description, image, openingHours, restaurantMessage }) => {
            const { isOpen, openingDetails } = handleOpeningHours(openingHours)
            return (
              <MenuItem key={id} href={`${location.pathname}/${id}`} name={name} description={description} image={image} isOpen={isOpen} openingDetails={openingDetails} message={restaurantMessage} />
            )
          })}
        </ul>
      </div>
    </Page>
  )
}

function mapState (state: RootState) {
  return {
    status: state.menus.status,
    menuItems: state.menus.items,
    store: state.store.current,
    deliveryType: state.cart.deliveryType,
    tableNumber: state.cart.tableNumber
  }
}

function mapDispatch (dispatch: Dispatch) {
  return {
    selectDeliveryType: dispatch.cart.selectDeliveryType,
    fetchActiveDeliveryMethods: dispatch.store.fetchActiveDeliveryMethods,
  }
}

export default connect(mapState, mapDispatch)(Menus)